import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { 
  ArrowLeft, TreeDeciduous, ChevronDown, ChevronUp, Check, BookOpen,
  Lightbulb, MessageCircle, Target, Map, Save, Loader2
} from "lucide-react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";

const AncestralModuleDetailPage = () => {
  const navigate = useNavigate();
  const { moduleId } = useParams();
  const { token } = useAuth();
  const [module, setModule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);
  const [reflections, setReflections] = useState({});
  const [savingReflection, setSavingReflection] = useState(null);
  
  // Expandable sections
  const [expandedIntro, setExpandedIntro] = useState(true);
  const [expandedConcepts, setExpandedConcepts] = useState(false);
  const [expandedPrompts, setExpandedPrompts] = useState(false);
  const [expandedPractice, setExpandedPractice] = useState(false);

  useEffect(() => {
    fetchModule();
    fetchReflections();
  }, [moduleId]);

  const fetchReflections = async () => {
    try {
      const response = await axios.get(`${API}/reflections/ancestral/${moduleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.reflections) {
        const reflectionMap = {};
        response.data.reflections.forEach(r => {
          reflectionMap[r.prompt_index] = r.content;
        });
        setReflections(reflectionMap);
      }
    } catch (error) {
      // No reflections yet, that's fine
    }
  };

  const saveReflection = async (promptIndex, prompt) => {
    const content = reflections[promptIndex];
    if (!content?.trim()) return;
    
    setSavingReflection(promptIndex);
    try {
      await axios.post(`${API}/reflections/save`, {
        module_type: 'ancestral_wisdom',
        module_id: parseInt(moduleId),
        prompt_index: promptIndex,
        prompt_text: prompt,
        content: content
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Reflection saved!");
    } catch (error) {
      toast.error("Failed to save reflection");
    } finally {
      setSavingReflection(null);
    }
  };

  const goToJournalWithPrompt = (prompt) => {
    navigate(`/journal?prompt=${encodeURIComponent(prompt)}`);
  };

  const fetchModule = async () => {
    try {
      const response = await axios.get(`${API}/ancestral-wisdom/modules/${moduleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setModule(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Premium subscription required for this session");
        navigate("/ancestral-wisdom");
      } else if (error.response?.status === 404) {
        toast.error("Session not found");
        navigate("/ancestral-wisdom");
      } else {
        console.error("Error fetching module:", error);
        toast.error("Unable to load session");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async () => {
    setCompleting(true);
    try {
      const response = await axios.post(
        `${API}/ancestral-wisdom/modules/${moduleId}/complete`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setModule(prev => ({ ...prev, is_completed: true }));
      
      if (response.data.badge_awarded) {
        toast.success("🌳 Congratulations! You've earned the Ancestral Wisdom Keeper badge!");
      } else {
        toast.success("Session completed! Continue exploring your ancestral wisdom.");
      }
    } catch (error) {
      toast.error("Unable to mark session as complete");
    } finally {
      setCompleting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen hero-gradient flex items-center justify-center">
        <div className="animate-breathe">
          <div className="w-16 h-16 rounded-full bg-amber-500/20 flex items-center justify-center">
            <TreeDeciduous className="w-8 h-8 text-amber-600" strokeWidth={1.5} />
          </div>
        </div>
      </div>
    );
  }

  if (!module) return null;

  return (
    <div className="min-h-screen hero-gradient" data-testid="ancestral-module-detail-page">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b border-border/50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate("/ancestral-wisdom")}
            className="text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            All Sessions
          </Button>
          <div className="flex items-center gap-2">
            <TreeDeciduous className="w-5 h-5 text-amber-600" strokeWidth={1.5} />
            <span className="text-sm text-muted-foreground">Session {module.id}</span>
          </div>
          <div className="w-24"></div>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="text-center mb-10">
          <span className="inline-block text-sm text-amber-600 bg-amber-100 dark:bg-amber-900/30 px-3 py-1 rounded-full mb-4">
            Session {module.id} • {module.estimated_time}
          </span>
          <h1 className="font-heading text-3xl md:text-4xl font-semibold text-foreground mb-3">
            {module.title}
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {module.subtitle}
          </p>
        </div>

        {/* Completion Status */}
        {module.is_completed && (
          <Card className="glass-card rounded-2xl border-0 mb-8 bg-amber-50/50 dark:bg-amber-950/20">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                <Check className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="font-medium text-foreground">Session Completed</p>
                <p className="text-sm text-muted-foreground">
                  Completed on {new Date(module.completed_at).toLocaleDateString()}
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Interactive Tool Notice */}
        {module.has_interactive_tool && (
          <Card className="glass-card rounded-2xl border-0 mb-8 bg-green-50/50 dark:bg-green-950/20">
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                  <Map className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="font-medium text-foreground">Interactive Tool Available</p>
                  <p className="text-sm text-muted-foreground">
                    This session includes the Family Mapping (Genogram) tool
                  </p>
                </div>
              </div>
              <Button 
                onClick={() => navigate("/family-map")}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                Open Tool
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Introduction Section */}
        <Card className="glass-card rounded-2xl border-0 mb-6">
          <CardContent className="p-0">
            <button
              className="w-full p-6 flex items-center justify-between text-left"
              onClick={() => setExpandedIntro(!expandedIntro)}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                  <BookOpen className="w-5 h-5 text-amber-600" />
                </div>
                <h2 className="font-heading text-xl font-semibold text-foreground">Introduction</h2>
              </div>
              {expandedIntro ? (
                <ChevronUp className="w-5 h-5 text-muted-foreground" />
              ) : (
                <ChevronDown className="w-5 h-5 text-muted-foreground" />
              )}
            </button>
            {expandedIntro && (
              <div className="px-6 pb-6">
                <div className="prose prose-amber dark:prose-invert max-w-none">
                  {module.introduction?.split('\n\n').map((paragraph, i) => (
                    <p key={i} className="text-foreground/90 leading-relaxed mb-4 last:mb-0">
                      {paragraph.trim()}
                    </p>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Key Concepts Section */}
        <Card className="glass-card rounded-2xl border-0 mb-6">
          <CardContent className="p-0">
            <button
              className="w-full p-6 flex items-center justify-between text-left"
              onClick={() => setExpandedConcepts(!expandedConcepts)}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                  <Lightbulb className="w-5 h-5 text-orange-600" />
                </div>
                <h2 className="font-heading text-xl font-semibold text-foreground">Key Concepts</h2>
              </div>
              {expandedConcepts ? (
                <ChevronUp className="w-5 h-5 text-muted-foreground" />
              ) : (
                <ChevronDown className="w-5 h-5 text-muted-foreground" />
              )}
            </button>
            {expandedConcepts && (
              <div className="px-6 pb-6 space-y-6">
                {module.key_concepts?.map((concept, i) => (
                  <div key={i} className="bg-muted/30 rounded-xl p-5">
                    <h3 className="font-heading text-lg font-semibold text-foreground mb-2">
                      {concept.title}
                    </h3>
                    <p className="text-foreground/80 leading-relaxed">
                      {concept.description}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Reflection Prompts Section */}
        <Card className="glass-card rounded-2xl border-0 mb-6">
          <CardContent className="p-0">
            <button
              className="w-full p-6 flex items-center justify-between text-left"
              onClick={() => setExpandedPrompts(!expandedPrompts)}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-rose-100 dark:bg-rose-900/30 flex items-center justify-center">
                  <MessageCircle className="w-5 h-5 text-rose-600" />
                </div>
                <h2 className="font-heading text-xl font-semibold text-foreground">Reflection Prompts</h2>
              </div>
              {expandedPrompts ? (
                <ChevronUp className="w-5 h-5 text-muted-foreground" />
              ) : (
                <ChevronDown className="w-5 h-5 text-muted-foreground" />
              )}
            </button>
            {expandedPrompts && (
              <div className="px-6 pb-6">
                <p className="text-muted-foreground mb-4">
                  Take time to journal or reflect on these questions:
                </p>
                <div className="space-y-6">
                  {module.reflection_prompts?.map((prompt, i) => (
                    <div key={i} className="bg-rose-50/50 dark:bg-rose-950/20 rounded-xl p-5">
                      <div className="flex items-start gap-3 mb-4">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-rose-100 dark:bg-rose-900/30 flex items-center justify-center text-xs font-medium text-rose-600">
                          {i + 1}
                        </span>
                        <p className="text-foreground/90 leading-relaxed italic">{prompt}</p>
                      </div>
                      
                      {/* Reflection Input */}
                      <div className="ml-9">
                        <Textarea
                          placeholder="Write your reflection here..."
                          value={reflections[i] || ""}
                          onChange={(e) => setReflections(prev => ({ ...prev, [i]: e.target.value }))}
                          className="min-h-[100px] rounded-xl mb-3 bg-white/50 dark:bg-white/5"
                          data-testid={`ancestral-reflection-textarea-${i}`}
                        />
                        <div className="flex gap-3 flex-wrap">
                          <Button
                            size="sm"
                            onClick={() => saveReflection(i, prompt)}
                            disabled={savingReflection === i || !reflections[i]?.trim()}
                            className="rounded-lg bg-rose-600 hover:bg-rose-700"
                            data-testid={`ancestral-save-reflection-btn-${i}`}
                          >
                            {savingReflection === i ? (
                              <Loader2 className="w-4 h-4 animate-spin mr-1" />
                            ) : (
                              <Save className="w-4 h-4 mr-1" />
                            )}
                            Save Reflection
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => goToJournalWithPrompt(prompt)}
                            className="rounded-lg"
                            data-testid={`ancestral-expand-journal-btn-${i}`}
                          >
                            <BookOpen className="w-4 h-4 mr-1" />
                            Expand in Journal
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Practice Section */}
        <Card className="glass-card rounded-2xl border-0 mb-8">
          <CardContent className="p-0">
            <button
              className="w-full p-6 flex items-center justify-between text-left"
              onClick={() => setExpandedPractice(!expandedPractice)}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                  <Target className="w-5 h-5 text-green-600" />
                </div>
                <h2 className="font-heading text-xl font-semibold text-foreground">Practice</h2>
              </div>
              {expandedPractice ? (
                <ChevronUp className="w-5 h-5 text-muted-foreground" />
              ) : (
                <ChevronDown className="w-5 h-5 text-muted-foreground" />
              )}
            </button>
            {expandedPractice && module.practice && (
              <div className="px-6 pb-6">
                <div className="bg-green-50/50 dark:bg-green-950/20 rounded-xl p-5">
                  <h3 className="font-heading text-lg font-semibold text-foreground mb-2">
                    {module.practice.title}
                  </h3>
                  <p className="text-foreground/90 leading-relaxed mb-4">
                    {module.practice.description}
                  </p>
                  <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
                    <span className="bg-green-100 dark:bg-green-900/30 px-3 py-1 rounded-full">
                      ⏱️ {module.practice.duration}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Complete Button */}
        {!module.is_completed && (
          <div className="text-center">
            <Button
              onClick={handleComplete}
              disabled={completing}
              className="bg-amber-600 hover:bg-amber-700 text-white px-8 py-3 rounded-full text-lg"
              data-testid="complete-session-btn"
            >
              {completing ? (
                <>
                  <TreeDeciduous className="w-5 h-5 mr-2 animate-spin" />
                  Completing...
                </>
              ) : (
                <>
                  <Check className="w-5 h-5 mr-2" />
                  Mark Session Complete
                </>
              )}
            </Button>
            <p className="text-sm text-muted-foreground mt-3">
              Complete all sections above before marking as done
            </p>
          </div>
        )}

        {/* Navigation between sessions */}
        <div className="flex justify-between mt-10 pt-6 border-t border-border/50">
          {module.id > 1 && (
            <Button
              variant="ghost"
              onClick={() => navigate(`/ancestral-wisdom/${module.id - 1}`)}
              className="text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Previous Session
            </Button>
          )}
          <div className="flex-1" />
          {module.id < 12 && (
            <Button
              variant="ghost"
              onClick={() => navigate(`/ancestral-wisdom/${module.id + 1}`)}
              className="text-muted-foreground hover:text-foreground"
            >
              Next Session
              <ArrowLeft className="w-4 h-4 ml-2 rotate-180" />
            </Button>
          )}
        </div>
      </main>
    </div>
  );
};

export default AncestralModuleDetailPage;
